import { MapPin } from 'lucide-react'
import type { WeddingSettings } from '@shared/settings'
import { fechaCompleta } from '../../../lib/format'
import { EnlaceExterno, Seccion, cx, tieneTexto, urlSegura } from './comunes'

/* ===========================================================================
   El evento — dónde, cuándo, cómo llegar y dónde dormir.
   Lo que un invitado de fuera de la ciudad necesita saber sin preguntar.
   =========================================================================== */

type Evento = WeddingSettings['event']

interface Lugar {
  clave: string
  etiqueta: string
  venue: string
  address: string
  time: string
  mapsUrl: string
}

function lugares(event: Evento): Lugar[] {
  return [
    {
      clave: 'ceremonia',
      etiqueta: 'Ceremonia',
      venue: event.ceremonyVenue,
      address: event.ceremonyAddress,
      time: event.ceremonyTime,
      mapsUrl: event.ceremonyMapsUrl,
    },
    {
      clave: 'recepcion',
      etiqueta: 'Recepción',
      venue: event.receptionVenue,
      address: event.receptionAddress,
      time: event.receptionTime,
      mapsUrl: event.receptionMapsUrl,
    },
  ].filter((l) => tieneTexto(l.venue) || tieneTexto(l.address) || tieneTexto(l.time))
}

export function hayEvento(event: Evento): boolean {
  return lugares(event).length > 0 || tieneTexto(event.lodging) || tieneTexto(event.transport)
}

interface Mapa {
  src: string
  title: string
}

/**
 * El mapa embebido de un lugar, o `null` si no hay nada que mapear.
 *
 * La consulta se arma siempre aquí, con `encodeURIComponent`, a partir de
 * "lugar, dirección". Nunca se reutiliza `mapsUrl` para el `src`: eso lo
 * escribe la pareja y podría ser cualquier cosa. `mapsUrl` solo cuenta como
 * señal de que el sitio está ubicado de verdad.
 *
 * Se usa `google.com/maps?...&output=embed` y no la Embed API oficial porque
 * esa exige clave y facturación, y nadie va a dar de alta una cuenta de nube
 * para que una tía vea dónde queda la iglesia.
 */
function mapaDe(sitio: Lugar): Mapa | null {
  // Sin dirección ni enlace guardado, el nombre del salón solo no ubica nada.
  if (!tieneTexto(sitio.address) && !tieneTexto(sitio.mapsUrl)) return null

  const partes = [sitio.venue.trim(), sitio.address.trim()].filter((p) => p !== '')
  if (partes.length === 0) return null

  const donde = sitio.venue.trim() !== '' ? sitio.venue.trim() : sitio.address.trim()
  return {
    src: `https://www.google.com/maps?q=${encodeURIComponent(partes.join(', '))}&output=embed`,
    title: `Mapa de la ${sitio.etiqueta.toLowerCase()} en ${donde}`,
  }
}

export default function EventoSeccion({
  event,
  weddingDate,
}: {
  event: Evento
  weddingDate: string | null
}) {
  const sitios = lugares(event)
  const fecha = fechaCompleta(weddingDate)

  const extras = [
    { clave: 'hospedaje', titulo: 'Dónde quedarse', texto: event.lodging },
    { clave: 'transporte', titulo: 'Cómo llegar', texto: event.transport },
  ].filter((e) => tieneTexto(e.texto))

  /* Mientras no haya ni dirección ni mapa en NINGÚN sitio, cada tarjeta son
     tres renglones cortos —etiqueta, «Por confirmar», hora— sosteniendo la
     estructura de una tarjeta con mapa dentro. En ese estado se aprietan y se
     ponen lado a lado ya en el teléfono. En cuanto la pareja escriba una
     dirección, la sección vuelve sola a su forma ancha.

     La condición mira todos los sitios a la vez, no cada uno: una tarjeta
     compacta junto a una completa se vería como un error de maquetación. */
  const sinUbicacion = sitios.every(
    (sitio) => !tieneTexto(sitio.address) && !tieneTexto(sitio.mapsUrl),
  )

  return (
    <Seccion id="evento" eyebrow="El evento" titulo="Dónde y cuándo" ancho="ancho">
      {fecha !== '' && <p className="mb-5 text-lg text-tinta/85">{fecha}</p>}

      {/* auto-fit: con un solo sitio configurado ocupa el ancho completo;
          con ceremonia y recepción, dos columnas como siempre. */}
      <div
        className={cx(
          'grid',
          sinUbicacion
            ? 'grid-cols-2 gap-x-5 gap-y-5'
            : 'gap-8 lg:grid-cols-[repeat(auto-fit,minmax(24rem,1fr))]',
        )}
      >
        {sitios.map((sitio) => {
          const mapa = urlSegura(sitio.mapsUrl)
          const embebido = mapaDe(sitio)
          return (
            <div key={sitio.clave} className={cx('border-t border-palo/70', sinUbicacion ? 'pt-4' : 'pt-6')}>
              <p className="eyebrow">{sitio.etiqueta}</p>
              {tieneTexto(sitio.venue) && (
                <h3
                  className={cx(
                    'mt-1 font-display text-tinta',
                    sinUbicacion ? 'text-lg' : 'mt-2 text-2xl',
                  )}
                >
                  {sitio.venue}
                </h3>
              )}
              {tieneTexto(sitio.time) && (
                <p className={cx('tabular font-mono text-sm text-tinta', sinUbicacion ? 'mt-1' : 'mt-2')}>
                  {sitio.time}
                </p>
              )}
              {tieneTexto(sitio.address) && (
                <p className="mt-2 max-w-md leading-relaxed text-tinta/85">{sitio.address}</p>
              )}
              {mapa && (
                <EnlaceExterno
                  href={mapa}
                  className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-laton underline underline-offset-4 hover:text-tinta"
                >
                  <MapPin size={16} aria-hidden="true" />
                  Ver en el mapa
                </EnlaceExterno>
              )}
              {embebido && (
                // La proporción está fijada de antemano: el mapa tarda en
                // llegar y el resto de la página no se mueve cuando llega.
                <div className="mt-5 aspect-[16/10] w-full overflow-hidden rounded-md border border-palo/70">
                  <iframe
                    src={embebido.src}
                    title={embebido.title}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    className="h-full w-full border-0"
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {extras.length > 0 && (
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {extras.map((extra) => (
            <div key={extra.clave} className="border-t border-palo/70 pt-6">
              <p className="eyebrow">{extra.titulo}</p>
              <p className="mt-3 max-w-xl whitespace-pre-line leading-relaxed text-tinta/85">
                {extra.texto}
              </p>
            </div>
          ))}
        </div>
      )}
    </Seccion>
  )
}
